import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Satellite, ShieldCheck, ClipboardCheck, ScanSearch, ArrowRight,
  Flame, MountainSnow, HardHat, Waves, Layers, Users, FileCheck2,
  Building2, Eye, Zap, GitBranch, Factory, Train, Anchor, Pickaxe,
  Radio, MapPin, CheckCircle2, AlertCircle, Info, Clock, TrendingUp,
  Lock, Fingerprint, Database, RefreshCw, Landmark,
} from 'lucide-react';
import { HeroProductVisual } from '@/components/public/HeroProductVisual';
import { InfrastructureIllustration } from '@/components/public/InfrastructureIllustration';
import { WorkflowStep } from '@/components/public/WorkflowStep';
import { EvidenceAlertVisual } from '@/components/public/EvidenceAlertVisual';
import { DashboardPreview } from '@/components/public/DashboardPreview';

const BG_HYDRO = 'https://images.unsplash.com/photo-1541544741938-0af808871cc0?auto=format&fit=crop&w=1800&q=80';
const BG_SAT = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1800&q=80';

export default function HomePage() {
  const nav = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="pb-0" data-testid="public-home">
      {/* ============================ HERO ============================ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(1200px 600px at 15% -10%, hsl(var(--primary) / 0.10), transparent 60%), radial-gradient(900px 500px at 100% 20%, hsl(var(--primary) / 0.06), transparent 60%), linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--secondary) / 0.35) 100%)',
          }}
        />
        <div className="absolute inset-0 -z-10 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-16 lg:pt-24 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary" data-testid="hero-eyebrow">
              <Satellite size={12} /> Space-Based Infrastructure Intelligence
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-[56px] font-semibold leading-[1.05] tracking-tight mt-5" data-testid="hero-headline">
              See emerging risks before they reach{' '}
              <span className="text-primary">critical infrastructure</span>.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-description">
              Aakash Drishti converts recurring satellite observations into evidence-backed intelligence for dams, hydropower projects and other critical infrastructure. It helps asset owners identify significant changes, validate emerging risks and coordinate action through a secure enterprise platform.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => nav('/request-demo')} data-testid="hero-primary-cta">
                Request Executive Demonstration
                <ArrowRight size={16} className="ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => {
                document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' });
              }} data-testid="hero-secondary-cta">
                Explore Platform Capabilities
              </Button>
            </div>
            <p className="mt-6 text-sm text-muted-foreground max-w-lg" data-testid="hero-trust">
              Designed for government organisations, public sector undertakings and operators of geographically distributed infrastructure.
            </p>
          </div>

          <div className="lg:col-span-6" data-testid="hero-visual">
            <HeroProductVisual scrollY={scrollY} />
          </div>
        </div>
      </section>

      {/* ============================ EXECUTIVE VALUE STRIP ============================ */}
      <section id="platform" className="border-y border-border bg-secondary/40" data-testid="value-strip">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Eye, title: 'Wider Visibility', desc: 'Monitor large and difficult-to-access infrastructure zones from a unified platform.' },
              { Icon: Clock, title: 'Earlier Awareness', desc: 'Identify significant environmental and physical changes from newly available observations.' },
              { Icon: FileCheck2, title: 'Evidence-Backed Decisions', desc: 'Review before-and-after imagery, affected areas, location context and confidence indicators.' },
              { Icon: CheckCircle2, title: 'Accountable Response', desc: 'Convert validated alerts into assigned, tracked and auditable incidents.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="shrink-0 rounded-lg bg-primary/10 text-primary p-2.5">
                  <Icon size={20} />
                </div>
                <div>
                  <div className="font-semibold text-[15px]">{title}</div>
                  <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ INFRASTRUCTURE VISIBILITY PROBLEM ============================ */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24" data-testid="problem-section">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">The visibility gap</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
              Critical risks often begin beyond the asset boundary.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Landslides, flooding, unauthorised development, terrain disturbance, fire and access-route disruption may originate kilometres away from the principal asset. Conventional inspection remains essential, but it cannot provide continuous visibility across every catchment, slope, corridor and protection zone.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Aakash Drishti adds a space-based observation layer to infrastructure monitoring, helping authorised teams identify where closer review or field verification may be required.
            </p>
            <div className="mt-6 rounded-lg border border-border bg-secondary/30 p-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Complementary, not a replacement:</span> Aakash Drishti works alongside physical inspection, instrumentation, SCADA, drone surveys and engineering analysis — not in place of them.
            </div>
          </div>
          <div className="lg:col-span-6">
            <InfrastructureIllustration />
          </div>
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section id="how-it-works" className="bg-secondary/30 border-y border-border" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">How Aakash Drishti works</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
              From observation to coordinated action.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Human validation remains part of every decision. AI assists detection; authorised users retain control over classification, escalation and closure.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <WorkflowStep
              index={1}
              Icon={Satellite}
              title="Observe"
              description="Recurring satellite and geospatial observations are collected for configured infrastructure zones."
              details={[
                'Asset boundary & sub-assets',
                'Configured monitoring zones',
                'Observation date & source',
                'Quality & cloud cover status',
              ]}
            />
            <WorkflowStep
              index={2}
              Icon={ScanSearch}
              title="Detect"
              description="AI-assisted analysis compares observations with approved historical and seasonal baselines."
              details={[
                'Water spread & shoreline change',
                'Terrain disturbance & landslides',
                'New construction & encroachment',
                'Fire and flood exposure',
              ]}
            />
            <WorkflowStep
              index={3}
              Icon={FileCheck2}
              title="Validate"
              description="Analysts review the location, imagery, confidence, affected area and operational context."
              details={[
                'AI Observation',
                'Under Analyst Review',
                'Probable Alert',
                'Validated Alert',
              ]}
            />
            <WorkflowStep
              index={4}
              Icon={ClipboardCheck}
              title="Act"
              description="Validated risks are assigned, escalated, verified and tracked through closure."
              details={[
                'Assigned officer & department',
                'Recommended action',
                'Field verification',
                'Incident timeline & closure',
              ]}
            />
          </div>
        </div>
      </section>

      {/* ============================ CORE MONITORING CAPABILITIES ============================ */}
      <section id="capabilities" className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24" data-testid="capabilities-section">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Core capabilities</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
            Infrastructure intelligence from every usable observation.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              Icon: Waves, title: 'Water Spread & Shoreline Change',
              points: ['Reservoir spread', 'Shoreline movement', 'River-width changes', 'Flooded areas near assets', 'Water accumulation on access routes'],
            },
            {
              Icon: MountainSnow, title: 'Landslide & Terrain Disturbance',
              points: ['Slope disturbance', 'Fresh exposed earth', 'Debris movement', 'Road-cutting instability', 'Terrain change near critical zones'],
            },
            {
              Icon: HardHat, title: 'Encroachment & New Construction',
              points: ['New structures', 'Land clearing & excavation', 'New roads', 'Settlement expansion', 'Activity in restricted zones'],
            },
            {
              Icon: Flame, title: 'Fire & Flood Exposure',
              points: ['Fire-event proximity', 'Flood extent', 'Affected roads', 'Exposed project facilities', 'Nearby settlements'],
            },
            {
              Icon: Layers, title: 'Before-and-After Evidence',
              points: ['Side-by-side imagery', 'Swipe comparison', 'Change overlays', 'Area measurement', 'Observation timeline'],
            },
            {
              Icon: ClipboardCheck, title: 'Alert & Incident Management',
              points: ['Analyst validation', 'Risk classification', 'Assignment & escalation', 'Field verification', 'Resolution & audit trail'],
            },
          ].map(({ Icon, title, points }) => (
            <Card key={title} className="p-6 border-border hover:border-primary/40 transition-colors group" data-testid={`capability-${title.split(' ')[0].toLowerCase()}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 text-primary p-2.5 group-hover:bg-primary/15 transition-colors">
                  <Icon size={22} />
                </div>
                <div className="font-display font-semibold text-[17px] leading-snug pt-0.5">{title}</div>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {points.map(p => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      {/* ============================ HYDROPOWER USE CASE ============================ */}
      <section id="hydropower" className="relative overflow-hidden" data-testid="hydropower-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(to right, hsl(var(--background)) 0%, hsl(var(--background)/0.85) 40%, hsl(var(--background)/0.5) 100%), url('${BG_HYDRO}')`,
            backgroundSize: 'cover', backgroundPosition: 'center',
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
              <Zap size={12} /> Primary use case · Hydropower
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-4 leading-tight">
              A wider field of view for hydropower infrastructure.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Hydropower projects depend on more than the powerhouse and dam structure. Reservoir conditions, catchment slopes, approach roads, downstream areas, project boundaries and nearby development can influence safety, accessibility and operations.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Aakash Drishti brings these geographically distributed areas into a unified monitoring environment.
            </p>
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <Card className="p-6 border-border/70 backdrop-blur-sm bg-background/85">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Monitored zones per project</div>
              <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {['Dam structure','Reservoir','Powerhouse','Spillway','Intake','Tailrace','Switchyard','Access roads','Sensitive slopes','Upstream catchment','Downstream impact zone','Reservoir protection area'].map(z => (
                  <div key={z} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/70" /> {z}
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-6 border-border/70 backdrop-blur-sm bg-background/85">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Realistic monitoring applications</div>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  ['Probable landslide near an access road', 'Terrain'],
                  ['Reservoir water-spread change', 'Water'],
                  ['Potential construction within a protected zone', 'Encroachment'],
                  ['Flood exposure near downstream infrastructure', 'Flood'],
                  ['Fire event near the project boundary', 'Fire'],
                  ['Terrain disturbance affecting accessibility', 'Terrain'],
                ].map(([text, tag]) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
                    <span className="flex-1">{text}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{tag}</Badge>
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-[11px] text-muted-foreground italic">
                All demonstration scenarios are marked as simulated inside the platform and do not represent actual events.
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ============================ EVIDENCE-FIRST ALERT DEMONSTRATION ============================ */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24" data-testid="evidence-section">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Evidence-first alert</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
            Every alert should explain what changed, where it changed and why it matters.
          </h2>
        </div>
        <div className="mt-10">
          <EvidenceAlertVisual />
        </div>
      </section>

      {/* ============================ ENTERPRISE & GOVERNMENT READINESS ============================ */}
      <section id="enterprise" className="bg-secondary/30 border-y border-border" data-testid="enterprise-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Enterprise & government readiness</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
              Designed for governed, accountable infrastructure monitoring.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { Icon: Lock, title: 'Organisation-Level Isolation', desc: 'Each organisation accesses only its own assets, observations, alerts, incidents and reports.' },
              { Icon: Users, title: 'Role-Based Access', desc: 'Administrators, asset managers, analysts, field teams and management users receive controlled access.' },
              { Icon: Fingerprint, title: 'Human Validation', desc: 'AI observations can be reviewed, classified, rejected, escalated or sent for field verification.' },
              { Icon: GitBranch, title: 'Complete Audit Trail', desc: 'Track who reviewed an alert, changed a classification, assigned an incident or approved closure.' },
              { Icon: Database, title: 'Data Governance', desc: 'Configurable retention, secure exports and deployment options suitable for enterprise requirements.' },
              { Icon: RefreshCw, title: 'Operational Continuity', desc: 'Previously processed observations remain available if an external imagery source is temporarily unavailable.' },
            ].map(({ Icon, title, desc }) => (
              <Card key={title} className="p-6 border-border">
                <div className="rounded-lg bg-primary/10 text-primary p-2.5 w-fit">
                  <Icon size={20} />
                </div>
                <div className="mt-4 font-semibold text-[15px]">{title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-xs text-muted-foreground max-w-2xl">
            Enterprise readiness terminology reflects platform design intent. Formal compliance certifications, where relevant, are pursued on a per-deployment basis with the operating organisation.
          </div>
        </div>
      </section>

      {/* ============================ MANAGEMENT DASHBOARD PREVIEW ============================ */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24" data-testid="dashboard-preview-section">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Management view</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
            A command view for senior decision-makers.
          </h2>
        </div>
        <div className="mt-10">
          <DashboardPreview />
        </div>
        <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
          Senior officials receive a concise portfolio view, while analysts and field teams can access the supporting imagery, evidence and operational workflow.
        </p>
      </section>

      {/* ============================ ADDITIONAL INFRASTRUCTURE SECTORS ============================ */}
      <section id="sectors" className="bg-secondary/30 border-y border-border" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Sectors</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
              One intelligence layer across distributed infrastructure.
            </h2>
            <p className="mt-4 text-muted-foreground">
              The platform architecture is designed to expand across sectors. Sector configurations range from production-ready to pilot to future expansion.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { Icon: Landmark, title: 'Dams and Reservoirs', desc: 'Catchment, shoreline, slope, downstream and protection-zone monitoring.', tag: 'MVP Capability' },
              { Icon: Zap, title: 'Transmission Networks', desc: 'Corridor encroachment, vegetation change, flooding, terrain disturbance and access risk.', tag: 'Pilot Configuration' },
              { Icon: Radio, title: 'Pipelines', desc: 'Right-of-way monitoring, excavation, land-use change, flood exposure and river-crossing changes.', tag: 'Pilot Configuration' },
              { Icon: Pickaxe, title: 'Mines & Tailings', desc: 'Pit expansion, tailings areas, slope conditions, water accumulation and surrounding land change.', tag: 'Pilot Configuration' },
              { Icon: Train, title: 'Railways & Highways', desc: 'Landslides, road or track access, flood exposure, encroachment and terrain change.', tag: 'Future Sector Expansion' },
              { Icon: Anchor, title: 'Ports & Coastal Assets', desc: 'Shoreline movement, flooding, land-use change, material storage and surrounding development.', tag: 'Future Sector Expansion' },
            ].map(({ Icon, title, desc, tag }) => (
              <Card key={title} className="p-6 border-border">
                <div className="flex items-start justify-between">
                  <div className="rounded-lg bg-primary/10 text-primary p-2.5">
                    <Icon size={20} />
                  </div>
                  <Badge variant="outline" className="text-[10px]">{tag}</Badge>
                </div>
                <div className="mt-4 font-semibold text-[15px]">{title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ PRODUCT DIFFERENTIATION ============================ */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-24" data-testid="differentiation-section">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Differentiation</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
            More than a satellite imagery viewer.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {[
            { title: 'Asset-Centred Monitoring', desc: 'Observations are organised around infrastructure assets, protection zones and operational priorities.' },
            { title: 'Contextual Detection', desc: 'Detected changes are evaluated based on location, distance, zone sensitivity and asset criticality.' },
            { title: 'Evidence Before Escalation', desc: 'Users can review baseline imagery, latest observations, change boundaries and evidence quality.' },
            { title: 'Human-Governed AI', desc: 'AI assists detection, while authorised users retain control over validation and action.' },
            { title: 'Alert-to-Closure Workflow', desc: 'Risks can move from observation to alert, incident, field verification and documented closure.' },
            { title: 'Digital Twin Readiness', desc: 'External geospatial intelligence layer for future 3D digital twin, BIM, IoT and SCADA integrations.' },
          ].map((it) => (
            <Card key={it.title} className="p-6 border-border">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={18} className="text-primary shrink-0" />
                <div className="font-semibold">{it.title}</div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed pl-8">{it.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ============================ ABOUT ANY TECH VENTURES ============================ */}
      <section id="about" className="bg-primary/5 border-y border-primary/15" data-testid="about-section">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-20 lg:py-24">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">About</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 leading-tight">
                Developed by Any Tech Ventures Pvt. Ltd.
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Aakash Drishti is a product of Any Tech Ventures Pvt. Ltd., an Indian emerging-technology company developing digital twin, geospatial, AI, AR, VR and infrastructure intelligence solutions for government organisations, public sector undertakings and enterprise asset owners.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                The product extends Any Tech Ventures’ infrastructure digitisation capabilities from visual representation to recurring observation, risk intelligence and coordinated response.
              </p>
              <div className="mt-6">
                <a href="https://anytechventures.com/" target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="about-atv-cta">
                    Visit Any Tech Ventures <ArrowRight size={14} className="ml-1" />
                  </Button>
                </a>
              </div>
            </div>
            <Card className="p-6 border-border/60 bg-background">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Capabilities offered</div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {['Digital twin & BIM', 'Geospatial intelligence', 'AI-assisted monitoring', 'AR / VR visualisation', 'Infrastructure digitisation'].map(x => (
                  <li key={x} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {x}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* ============================ EXECUTIVE DEMONSTRATION CTA ============================ */}
      <section className="relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(120deg, hsl(var(--background)) 0%, hsl(var(--background)/0.7) 55%, hsl(var(--background)/0.4) 100%), url('${BG_SAT}')`,
            backgroundSize: 'cover', backgroundPosition: 'center',
          }} />
        </div>
        <div className="max-w-5xl mx-auto text-center py-20 px-4">
          <ShieldCheck size={36} className="text-primary mx-auto" />
          <h3 className="font-display text-3xl md:text-[40px] font-semibold mt-4 leading-tight">
            Bring space-based visibility to your infrastructure portfolio.
          </h3>
          <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Request an executive demonstration to explore how Aakash Drishti can be configured for a hydropower project, dam, transmission corridor, pipeline, mine or other distributed infrastructure asset.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => nav('/request-demo')} data-testid="final-cta-demo">
              Request Executive Demonstration <ArrowRight size={16} className="ml-1" />
            </Button>
            <a href="https://anytechventures.com/" target="_blank" rel="noreferrer">
              <Button size="lg" variant="outline" data-testid="final-cta-atv">
                Contact Any Tech Ventures
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
